import { z } from 'zod';

export type MeetingAppBundle = {
  bundleId: string;
  name: string;
};

export type MeetingAppGroup = {
  name: string;
  keys: string[];
};

// KEEP IN SYNC with MeetingAppAllowlistStore.fallbackBundles in
// apps/desktop/ContextKit/Sources/observer/lib/Meetings/MeetingDetection/MeetingAppAllowlistStore.swift
export const DEFAULT_MEETING_APP_BUNDLES: MeetingAppBundle[] = [
  { bundleId: 'com.google.Chrome', name: 'Google Chrome' },
  { bundleId: 'com.google.Chrome.helper', name: 'Google Chrome' },
  { bundleId: 'com.apple.WebKit.GPU', name: 'Safari' },
  { bundleId: 'com.operasoftware.Opera.helper', name: 'Opera' },
  { bundleId: 'org.mozilla.firefox', name: 'Firefox' },
  { bundleId: 'company.thebrowser.browser.helper', name: 'Arc/Dia' },
  { bundleId: 'com.brave.Browser.helper', name: 'Brave Browser' },
  { bundleId: 'com.microsoft.edgemac.helper', name: 'Microsoft Edge' },
  { bundleId: 'ai.perplexity.comet.helper', name: 'Perplexity Comet' },
  { bundleId: 'com.openai.atlas.web.helper', name: 'OpenAI Atlas' },
  { bundleId: 'com.vivaldi.Vivaldi.helper', name: 'Vivaldi' },
  { bundleId: 'net.imput.helium.helper', name: 'Helium' },
  { bundleId: 'net.whatsapp.WhatsApp', name: 'WhatsApp' },
  { bundleId: 'com.tinyspeck.slackmacgap.helper', name: 'Slack' },
  { bundleId: 'us.zoom.xos', name: 'Zoom' },
  { bundleId: 'com.microsoft.teams2', name: 'Microsoft Teams' },
  { bundleId: 'com.microsoft.teams2.modulehost', name: 'Microsoft Teams' },
  { bundleId: 'com.microsoft.teams2.helper', name: 'Microsoft Teams' },
  { bundleId: 'com.apple.avconferenced', name: 'FaceTime' },
];

const meetingAppBundlesPayloadSchema = z.object({
  meeting_app_bundle_ids: z.array(
    z.object({
      bundle_id: z.string(),
      name: z.string(),
    })
  ),
});

export const normalizeMeetingAppBundleKey = (bundleId: string): string =>
  bundleId.trim().toLowerCase();

export const resolveBaseMeetingApps = (
  payload: unknown
): MeetingAppBundle[] => {
  const parsed = meetingAppBundlesPayloadSchema.safeParse(payload);
  if (!parsed.success) return DEFAULT_MEETING_APP_BUNDLES;

  return parsed.data.meeting_app_bundle_ids.map((entry) => ({
    bundleId: entry.bundle_id,
    name: entry.name,
  }));
};

export const groupMeetingAppsByName = (
  apps: MeetingAppBundle[]
): MeetingAppGroup[] => {
  const keysByName = new Map<string, string[]>();

  for (const app of apps) {
    const key = normalizeMeetingAppBundleKey(app.bundleId);
    if (!key) continue;

    const keys = keysByName.get(app.name);
    if (!keys) {
      keysByName.set(app.name, [key]);
    } else if (!keys.includes(key)) {
      keys.push(key);
    }
  }

  return [...keysByName.entries()].map(([name, keys]) => ({ name, keys }));
};

export const getMeetingAppGroupKeys = (
  apps: MeetingAppBundle[],
  appName: string,
  bundleId: string
): string[] => {
  const normalizedBundleId = normalizeMeetingAppBundleKey(bundleId);
  if (!normalizedBundleId) return [];

  const appGroups = groupMeetingAppsByName(apps);
  const matchingGroup =
    appGroups.find((group) => group.keys.includes(normalizedBundleId)) ??
    appGroups.find((group) => group.name === appName);

  return matchingGroup
    ? [...new Set([...matchingGroup.keys, normalizedBundleId])]
    : [normalizedBundleId];
};
